import { ChoiceMenu } from 'components/eventView/filters/ChoiceMenu';
import { EventEntityCategory } from 'components/eventView/pages/eventData';
import { ActionButton } from 'elements/eventView/atomic/ActionButton';
import { ScrollHorizontal } from 'elements/functional/ScrollHorizontal';
import { useAnalytics } from 'libs/utils/site/analytics';
import { chain } from 'lodash';
import { useContext } from 'react';
import { CtxEventView } from 'session/ctxEventView';
import { Localization } from 'static/localization/localization';
import styled, { css } from 'styled-components/macro';

interface Props {
    className?: string;
    isWide: boolean;
}

export const ProgramCategoryFilterRow = (props: Props) => {
    const ctxEventView = useContext(CtxEventView);
    const { programCategoryIdsFilter, programDayFilter, allPrograms, categories } = ctxEventView;
    const analytics = useAnalytics();
    const { isWide } = props;

    const filterCategoryIds = chain(allPrograms)
        .filter((e) => (programDayFilter ? e.dateKey === programDayFilter : true))
        .flatMap((e) => e.categoryIds ?? [])
        .uniq()
        .filter(Boolean)
        .value();

    const filterCategories = categories.filter((e) => filterCategoryIds.includes(e.categoryId));

    function handleClick(category: EventEntityCategory) {
        analytics.track('event_ui.filter.clicked', {
            id: category.categoryId,
            title: category.title,
            type: 'category'
        });

        ctxEventView.setProgramCategoryIdsFilter([category.categoryId]);
    }

    function handleReset() {
        ctxEventView.setProgramCategoryIdsFilter(null);
    }

    if (ctxEventView.programSearchTerm) {
        return;
    }

    if (filterCategories.length <= 1) {
        return null;
    }

    return (
        <S_ProgramCategoryFilterRow $isWide={isWide} className={props.className}>
            {isWide && (
                <>
                    <ChoiceMenu
                        selectedIndex={
                            programCategoryIdsFilter == null
                                ? 0
                                : filterCategories.findIndex((category) =>
                                      programCategoryIdsFilter?.includes(category.categoryId)
                                  ) + 1
                        }
                        items={[
                            Localization.ALL,
                            ...filterCategories.map((category) => {
                                return category.title;
                            })
                        ]}
                        onSelected={(index) => {
                            if (index === 0) {
                                handleReset();
                            } else {
                                handleClick(filterCategories[index - 1]);
                            }
                        }}
                    />
                </>
            )}
            {!isWide && (
                <S_ScrollHorizontal>
                    <S_Buttons $isWide={isWide}>
                        <ActionButton
                            text={'All'}
                            highlighted={ctxEventView.programCategoryIdsFilter === null}
                            onClick={handleReset}
                        />
                        {filterCategories.map((category) => {
                            return (
                                <ActionButton
                                    key={category.categoryId}
                                    text={category.title}
                                    highlighted={programCategoryIdsFilter?.includes(
                                        category.categoryId
                                    )}
                                    onClick={() => {
                                        handleClick(category);
                                    }}
                                />
                            );
                        })}
                    </S_Buttons>
                </S_ScrollHorizontal>
            )}
        </S_ProgramCategoryFilterRow>
    );
};

const S_ProgramCategoryFilterRow = styled.div<{ $isWide: boolean }>`
    background: #fff;
    box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.1);
    height: 64px;
    display: flex;
    align-items: center;
    ${(props) => {
        if (props.$isWide) {
            return css`
                box-shadow: none;
                height: unset;
                background: unset;
                margin: 0 0 50px 0;
            `;
        }
    }}
`;

const S_Buttons = styled.div<{ $isWide: boolean }>`
    display: flex;
    flex-direction: row;
    gap: 10px;
    height: 100%;
    padding: 10px 12px;
    ${(props) => {
        if (props.$isWide) {
            return css`
                flex-wrap: wrap;
            `;
        }
    }}
`;

const S_ScrollHorizontal = styled(ScrollHorizontal)`
    ::-webkit-scrollbar {
        display: none;
    }
`;
